import { SapEnvironment } from 'src/app/models';

export interface ISessionPersist {
  currentRole: string;
  ui: {
    selectedSapEnvironment: SapEnvironment;
    sideMenu: {
      isCollapsed: boolean;
    };
  };
}

/**
 * Key and payload shape are Akita's, deliberately unchanged. `persistState` wrote
 * `{ [storeName]: <value at the include path> }`, so `include: ['session.persist']`
 * produced `{ session: <the persist object>, $cache: {…} }`.
 *
 * Reading the same key back is what stops the migration silently resetting every
 * user's sidebar state and SAP environment. Renaming it would need a one-time
 * migration and buys nothing.
 */
const STORAGE_KEY = 'EMMET.AKITA';
const STORE_NAME = 'session';

export function createInitialPersist(): ISessionPersist {
  return {
    currentRole: '',
    ui: {
      selectedSapEnvironment: SapEnvironment.Q,
      sideMenu: { isCollapsed: false },
    },
  };
}

/**
 * Merged field-by-field rather than spread wholesale: a payload written by an older
 * build can be missing keys the app now reads, and `persist.ui.sideMenu.isCollapsed`
 * is dereferenced unguarded by consumers.
 */
export function readPersistedSession(storage: Storage = localStorage): ISessionPersist {
  const initial = createInitialPersist();
  const stored = parse(storage)[STORE_NAME];

  if (!stored) {
    return initial;
  }

  const ui = stored.ui;

  return {
    currentRole: stored.currentRole ?? initial.currentRole,
    ui: {
      selectedSapEnvironment: ui?.selectedSapEnvironment ?? initial.ui.selectedSapEnvironment,
      sideMenu: {
        isCollapsed: ui?.sideMenu?.isCollapsed ?? initial.ui.sideMenu.isCollapsed,
      },
    },
  };
}

/** Preserves sibling keys (notably Akita's `$cache`) so nothing else is clobbered. */
export function writePersistedSession(
  persist: ISessionPersist,
  storage: Storage = localStorage
): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({ ...parse(storage), [STORE_NAME]: persist }));
  } catch {
    // A full or unavailable storage must not take the app down — the preference is
    // simply not remembered, which is what persistState effectively did too.
  }
}

type StoredShape = Partial<Record<string, DeepPartial<ISessionPersist>>>;

type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] };

function parse(storage: Storage): StoredShape {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(STORAGE_KEY) ?? '{}');

    return isPlainObject(parsed) ? (parsed as StoredShape) : {};
  } catch {
    return {};
  }
}

// Not redundant: valid JSON also parses to strings, numbers and arrays, and
// `writePersistedSession` spreads whatever it gets — a corrupted `"abc"` would be
// written back as `{"0":"a","1":"b","2":"c"}`.
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && !!value && !Array.isArray(value);
}
